import {
  type InteractionResolver,
  parseStructuredContentFromModelOutput,
} from "@repo/app-types/content/structured/parser";
import { type FC, useMemo } from "react";
import { trimPartialTags } from "../markdown/tagPlugin.js";
import {
  renderStructuredContent,
  type StructuredContentRenderContext,
} from "./structuredContentRenderer.js";

export interface ModelOutputContentProps {
  /** The raw model output: Markdown with our XML reference tags. */
  text: string;
  /**
   * Whether the message is still streaming in. While streaming, a trailing
   * partial tag (e.g. `<quote interac`) is trimmed instead of shown verbatim.
   */
  isStreaming?: boolean;
  /**
   * Resolves the interaction ids cited in `<quote>`/`<customer>` tags,
   * typically from the tool results present in the same thread. Resolved
   * quotes and references render as interactive elements when the host app
   * provides a `GradingReferenceProvider`; unresolved ones render inert.
   */
  resolveInteractionId: InteractionResolver;
  /** Context for resolving references whose nodes don't carry their own. */
  context?: StructuredContentRenderContext;
}

const emptyContext: StructuredContentRenderContext = {};

/**
 * Renders an agent chat message through the shared structured content
 * pipeline: repairs and parses the model's Markdown (including GFM tables) and
 * our `<quote>`/`<customer>`/`<person>`/`<teammate>`/`<red>`/`<green>` tags
 * into the structured content AST, then renders it with the shared renderer —
 * the same parsing and rendering used for saved automation artifacts and
 * coaching notes.
 */
export const ModelOutputContent: FC<ModelOutputContentProps> = (props) => {
  const { text, isStreaming = false, resolveInteractionId } = props;

  const content = useMemo(
    () =>
      parseStructuredContentFromModelOutput(
        isStreaming ? trimPartialTags(text) : text,
        resolveInteractionId,
      ),
    [text, isStreaming, resolveInteractionId],
  );

  return <>{renderStructuredContent(content, props.context ?? emptyContext)}</>;
};
